import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import css from './note.module.css';
import commonCss from '../common/common.module.css';
import type {QuizQuestion} from '../../types/Note';
import {generateQuiz} from '../../services/functionsService';
import {useToast} from '../../context/ToastProvider';
import {useUpgrade} from '../../context/UpgradeProvider';
import {Button, Spinner} from '../common/ui';
import posthog, {isPostHogConfigured} from '../../posthog';

// Two data sources: the authed note page passes `noteId` (quiz generated /
// cached via callable), the public share page passes `initialQuestions`
// straight from the share doc (no auth, no callable).
export function QuizTab({
  noteId,
  initialQuestions,
}: {
  noteId?: string;
  initialQuestions?: QuizQuestion[] | null;
}) {
  const {t} = useTranslation();
  const showToast = useToast();
  const {handleLimitError} = useUpgrade();

  const [questions, setQuestions] = useState<QuizQuestion[] | null>(
    initialQuestions ?? null,
  );
  const [loading, setLoading] = useState(!initialQuestions);
  const [failed, setFailed] = useState(false);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const load = useCallback(async () => {
    if (!noteId) {
      return;
    }
    setLoading(true);
    setFailed(false);
    try {
      // Server caches the quiz in the note's artifacts, so repeat visits
      // resolve instantly.
      const result = await generateQuiz(noteId);
      setQuestions(result.questions);
    } catch (e) {
      if (!handleLimitError(e)) {
        console.warn(e);
        showToast(t('ErrorGenerateQuiz'), 'error');
      }
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [noteId, handleLimitError, showToast, t]);

  useEffect(() => {
    if (!initialQuestions) {
      void load();
    }
  }, [load, initialQuestions]);

  const restart = () => {
    setIndex(0);
    setSelected(null);
    setScore(0);
    setFinished(false);
  };

  if (loading) {
    return (
      <div className={commonCss.emptyState}>
        <Spinner large />
        <p>{t('QuizLoading')}</p>
      </div>
    );
  }
  if (failed || !questions || questions.length === 0) {
    return (
      <div className={commonCss.emptyState}>
        <p>{t('ErrorGenerateQuizText')}</p>
        {noteId && (
          <Button variant="primary" icon="refresh" onClick={load}>
            {t('TryAgain')}
          </Button>
        )}
      </div>
    );
  }

  if (finished) {
    const percent = Math.round((score / questions.length) * 100);
    return (
      <div className={commonCss.emptyState}>
        <h2 style={{margin: 0, color: 'var(--color-text-primary)'}}>
          {t('QuizCompleted')}
        </h2>
        <p className={css.quizScore}>
          {score}/{questions.length}
        </p>
        <p>
          {t('YourScore')} {percent}%{percent < 60 ? ` — ${t('KeepPracticing')}` : ''}
        </p>
        <Button variant="primary" icon="refresh" onClick={restart}>
          {t('RestartQuiz')}
        </Button>
      </div>
    );
  }

  const question = questions[index];
  const answered = selected !== null;

  const choose = (option: string) => {
    if (answered) {
      return;
    }
    setSelected(option);
    if (option === question.correct_answer) {
      setScore(s => s + 1);
    }
  };

  const next = () => {
    if (index + 1 >= questions.length) {
      if (isPostHogConfigured) {
        posthog.capture('quiz_completed', {
          score,
          question_count: questions.length,
        });
      }
      setFinished(true);
    } else {
      setIndex(i => i + 1);
      setSelected(null);
    }
  };

  return (
    <div className={css.quizWrap}>
      <p className={css.quizProgress}>
        {t('Question')} {index + 1} / {questions.length}
      </p>
      <h3 className={css.quizQuestion}>{question.question}</h3>
      {question.options.map(option => {
        let cls = css.quizOption;
        if (answered) {
          if (option === question.correct_answer) {
            cls = css.quizOptionCorrect;
          } else if (option === selected) {
            cls = css.quizOptionWrong;
          }
        }
        return (
          <button
            key={option}
            className={cls}
            onClick={() => choose(option)}
            type="button"
          >
            {option}
          </button>
        );
      })}
      <div className={css.quizFooter}>
        <span className={css.quizProgress}>
          {answered &&
            (selected === question.correct_answer
              ? t('CorrectAnswer')
              : t('IncorrectAnswer'))}
        </span>
        <Button variant="primary" onClick={next} disabled={!answered}>
          {t('NextQuestion')}
        </Button>
      </div>
    </div>
  );
}
